import React, { useState, useEffect } from 'react'
import { cn } from '../../lib/utils'

function Quote() {
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    projectType: '',
    budget: '',
    timeline: '',
    description: '',
    requirements: ''
  })

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null)

  const validateForm = () => {
    const newErrors = {}

    if (!formData.companyName.trim()) {
      newErrors.companyName = 'Nome da empresa é obrigatório'
    }
    if (!formData.contactName.trim()) {
      newErrors.contactName = 'Nome do contato é obrigatório'
    }
    if (!formData.email.trim()) {
      newErrors.email = 'Email é obrigatório'
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email deve ter um formato válido'
    }
    if (!formData.projectType) {
      newErrors.projectType = 'Tipo de projeto é obrigatório'
    }
    if (!formData.description.trim()) {
      newErrors.description = 'Descrição do projeto é obrigatória'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setIsSubmitting(true)
    setSubmitStatus(null)

    try {
      const response = await fetch('/api/v1/quotes', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]').content
        },
        body: JSON.stringify({
          quote: {
            name: formData.contactName,
            email: formData.email,
            phone: formData.phone,
            company: formData.companyName,
            service: formData.projectType,
            message: formData.description,
            project_type: formData.projectType,
            budget: formData.budget,
            timeline: formData.timeline,
            description: formData.description,
            requirements: formData.requirements
          }
        })
      })

      const result = await response.json()

      if (result.success) {
        setSubmitStatus({
          type: 'success',
          message: result.message || 'Solicitação enviada com sucesso! Entraremos em contato em até 24 horas.'
        })
        
        // Limpar formulário
        setFormData({
          companyName: '',
          contactName: '',
          email: '',
          phone: '',
          projectType: '',
          budget: '',
          timeline: '',
          description: '',
          requirements: ''
        })
      } else {
        setSubmitStatus({
          type: 'error',
          message: result.message || 'Erro ao enviar solicitação'
        })
      }
    } catch (error) {
      setSubmitStatus({
        type: 'error',
        message: 'Erro ao enviar solicitação. Tente novamente.'
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="page quote-page">
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-[#2F5496] to-[#1e40af] py-20 relative overflow-hidden">
        {/* Decorative background elements */}
        <div className="absolute inset-0 bg-gradient-to-r from-blue-600/20 to-indigo-700/20"></div>
        <div className="absolute top-0 left-0 w-full h-full bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iNCIvPjwvZz48L2c+PC9zdmc+')] opacity-30"></div>
        {/* Additional floating elements */}
        <div className="absolute top-20 left-20 w-6 h-6 bg-white/20 rounded-full animate-bounce"></div>
        <div className="absolute bottom-20 right-20 w-4 h-4 bg-white/30 rounded-full animate-pulse"></div>
        <div className="absolute top-1/2 left-10 w-3 h-3 bg-white/25 rounded-full animate-ping"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-5xl font-bold text-white mb-6">
            Solicitar Orçamento
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
            Transforme sua ideia em realidade. Preencha o formulário abaixo e receba um orçamento personalizado para seu projeto tecnológico.
          </p>
        </div>
      </div>

      {/* Form Section */}
      <div className="container mx-auto px-4 py-20">
        <div className="max-w-4xl mx-auto">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl shadow-xl p-8">
                <h2 className="text-3xl font-bold mb-6 text-gray-900">Detalhes do Projeto</h2>

                {submitStatus && (
                  <div
                    className={cn(
                      'mb-6 p-4 rounded-lg',
                      submitStatus.type === 'success'
                        ? 'bg-green-100 text-green-800 border border-green-200'
                        : 'bg-red-100 text-red-800 border border-red-200'
                    )}
                  >
                    {submitStatus.message}
                  </div>
                )}

                <form onSubmit={handleSubmit} noValidate>
                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-2">
                        Nome da Empresa *
                      </label>
                      <input
                        type="text"
                        id="companyName"
                        name="companyName"
                        value={formData.companyName}
                        onChange={handleChange}
                        className={cn(
                          'w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#2F5496] focus:border-transparent transition-colors',
                          errors.companyName ? 'border-red-500' : 'border-gray-300'
                        )}
                        placeholder="Sua empresa"
                      />
                      {errors.companyName && (
                        <p className="mt-1 text-sm text-red-600">{errors.companyName}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="contactName" className="block text-sm font-medium text-gray-700 mb-2">
                        Nome do Contato *
                      </label>
                      <input
                        type="text"
                        id="contactName"
                        name="contactName"
                        value={formData.contactName}
                        onChange={handleChange}
                        className={cn(
                          'w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#2F5496] focus:border-transparent transition-colors',
                          errors.contactName ? 'border-red-500' : 'border-gray-300'
                        )}
                        placeholder="Seu nome completo"
                      />
                      {errors.contactName && (
                        <p className="mt-1 text-sm text-red-600">{errors.contactName}</p>
                      )}
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                        Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className={cn(
                          'w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#2F5496] focus:border-transparent transition-colors',
                          errors.email ? 'border-red-500' : 'border-gray-300'
                        )}
                        placeholder="seu@email.com"
                      />
                      {errors.email && (
                        <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                        Telefone
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F5496] focus:border-transparent transition-colors"
                        placeholder="(48) 98444-8798"
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-6 mb-6">
                    <div>
                      <label htmlFor="projectType" className="block text-sm font-medium text-gray-700 mb-2">
                        Tipo de Projeto *
                      </label>
                      <select
                        id="projectType"
                        name="projectType"
                        value={formData.projectType}
                        onChange={handleChange}
                        className={cn(
                          'w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#2F5496] focus:border-transparent transition-colors',
                          errors.projectType ? 'border-red-500' : 'border-gray-300'
                        )}
                      >
                        <option value="">Selecione o tipo</option>
                        <option value="website">Website Corporativo</option>
                        <option value="ecommerce">E-commerce</option>
                        <option value="mobile">Aplicativo Mobile</option>
                        <option value="webapp">Aplicação Web</option>
                        <option value="consulting">Consultoria</option>
                        <option value="other">Outro</option>
                      </select>
                      {errors.projectType && (
                        <p className="mt-1 text-sm text-red-600">{errors.projectType}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="budget" className="block text-sm font-medium text-gray-700 mb-2">
                        Orçamento Estimado
                      </label>
                      <select
                        id="budget"
                        name="budget"
                        value={formData.budget}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F5496] focus:border-transparent transition-colors"
                      >
                        <option value="">Selecione a faixa</option>
                        <option value="5k-15k">R$ 5.000 - R$ 15.000</option>
                        <option value="15k-50k">R$ 15.000 - R$ 50.000</option>
                        <option value="50k-100k">R$ 50.000 - R$ 100.000</option>
                        <option value="100k+">R$ 100.000+</option>
                      </select>
                    </div>
                  </div>

                  <div className="mb-6">
                    <label htmlFor="timeline" className="block text-sm font-medium text-gray-700 mb-2">
                      Prazo Desejado
                    </label>
                    <select
                      id="timeline"
                      name="timeline"
                      value={formData.timeline}
                      onChange={handleChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F5496] focus:border-transparent transition-colors"
                    >
                      <option value="">Selecione o prazo</option>
                      <option value="1month">1 mês</option>
                      <option value="3months">3 meses</option>
                      <option value="6months">6 meses</option>
                      <option value="1year">1 ano</option>
                      <option value="flexible">Flexível</option>
                    </select>
                  </div>

                  <div className="mb-6">
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                      Descrição do Projeto *
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows="5"
                      className={cn(
                        'w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-[#2F5496] focus:border-transparent transition-colors resize-none',
                        errors.description ? 'border-red-500' : 'border-gray-300'
                      )}
                      placeholder="Descreva detalhadamente seu projeto, objetivos e funcionalidades desejadas..."
                    />
                    {errors.description && (
                      <p className="mt-1 text-sm text-red-600">{errors.description}</p>
                    )}
                  </div>

                  <div className="mb-8">
                    <label htmlFor="requirements" className="block text-sm font-medium text-gray-700 mb-2">
                      Requisitos Técnicos Específicos
                    </label>
                    <textarea
                      id="requirements"
                      name="requirements"
                      value={formData.requirements}
                      onChange={handleChange}
                      rows="4"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2F5496] focus:border-transparent transition-colors resize-none"
                      placeholder="Mencione tecnologias específicas, integrações, performance, etc..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={cn(
                      'w-full py-4 px-6 rounded-lg font-bold text-white transition-all text-lg',
                      isSubmitting
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-[#2F5496] hover:bg-[#1e40af] hover:shadow-xl transform hover:scale-[1.02]'
                    )}
                  >
                    {isSubmitting ? 'Enviando Solicitação...' : 'Solicitar Orçamento'}
                  </button>
                </form>
              </div>
            </div>

            {/* Sidebar */}
            <div className="lg:col-span-1">
              <div className="bg-gradient-to-br from-[#2F5496] to-[#1e40af] rounded-2xl p-8 text-white">
                <h3 className="text-2xl font-bold mb-6">Por que escolher a Prata?</h3>
                <ul className="space-y-4">
                  <li className="flex items-start">
                    <span className="text-green-400 mr-3 text-xl">✓</span>
                    <span>Orçamento detalhado em 24 horas</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-400 mr-3 text-xl">✓</span>
                    <span>Consultoria gratuita na definição do projeto</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-400 mr-3 text-xl">✓</span>
                    <span>Suporte técnico durante todo o desenvolvimento</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-green-400 mr-3 text-xl">✓</span>
                    <span>Garantia de qualidade e entrega no prazo</span>
                  </li>
                </ul>

                <div className="mt-8 p-4 bg-white/10 rounded-lg">
                  <h4 className="font-bold mb-2">Precisa de ajuda?</h4>
                  <p className="text-sm text-blue-100 mb-3">
                    Nossa equipe está pronta para esclarecer suas dúvidas.
                  </p>
                  <p className="text-sm">
                    📧 ceo@prata.dev.br<br />
                    📱 (48) 98444-8798
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Quote